/**
 * Keeps the Klaviyo giveaway popup looking like its Klaviyo editor.
 *
 * Klaviyo styles the form with emotion classes (`.goNNN` repeated three times,
 * no !important). The theme's button and input reset in app.min.css and the
 * Tailwind play CDN carry about eight ids of specificity, so they win and the
 * form loses font size, letter spacing, line height and height on the site
 * while the editor still looks right.
 *
 * Restating the design in theme CSS does not hold: those class hashes change
 * every time the form is edited in Klaviyo. So instead of guessing values, copy
 * Klaviyo's own declarations onto the element as inline !important, which
 * outranks any stylesheet.
 *
 * Scoped to one form id on purpose. The older forms (back-in-stock, SMS,
 * signup) are painted by hand in layout/theme.liquid, app.min.css and
 * custom.css; letting Klaviyo win there would change how they look today.
 * To cover another form, add its id: it is in the markup as
 * data-testid="klaviyo-form-<id>" and in the form's Klaviyo URL.
 */
(() => {
  const FORM_IDS = ['UFVzNZ'];
  const FORMS = FORM_IDS.map((id) => `form[data-testid="klaviyo-form-${id}"]`).join(',');
  const TARGETS = 'button, input, select, textarea';
  const HASH = /^(?:\.go\d+)+$/;

  // Klaviyo's base rules only, no pseudo-classes: a hover or focus state must
  // stay a state instead of becoming the element's permanent inline style.
  const collect = () => {
    const found = [];
    for (const sheet of document.styleSheets) {
      let list;
      try {
        list = sheet.cssRules;
      } catch (error) {
        continue; // cross-origin stylesheet
      }
      for (const rule of list) {
        if (!rule.selectorText || !HASH.test(rule.selectorText)) continue;
        const hashes = [...new Set(rule.selectorText.match(/\.go\d+/g))];
        const declarations = [];
        for (const property of rule.style) {
          declarations.push([property, rule.style.getPropertyValue(property)]);
        }
        if (declarations.length) found.push({ hashes, declarations });
      }
    }
    return found;
  };

  // The element is only marked done once something was actually copied onto it:
  // Klaviyo inserts its <style> tags while the form renders, so the first pass
  // over a fresh button can run before its rule exists.
  const apply = (rules, element) => {
    const classes = [...element.classList];
    const signature = classes.join(' ');
    if (element.dataset.klGuard === signature) return;

    let matched = false;
    for (const { hashes, declarations } of rules) {
      if (!hashes.every((hash) => classes.includes(hash.slice(1)))) continue;
      for (const [property, value] of declarations) {
        element.style.setProperty(property, value, 'important');
      }
      matched = true;
    }
    if (matched) element.dataset.klGuard = signature;
  };

  const run = () => {
    const forms = document.querySelectorAll(FORMS);
    if (!forms.length) return;

    // Reading the stylesheets costs more than walking the form, and the form
    // mutates on every step and animation frame, so look for work first.
    const pending = [];
    const queue = (element) => {
      if (element.dataset.klGuard !== [...element.classList].join(' ')) pending.push(element);
    };
    forms.forEach((form) => {
      queue(form);
      form.querySelectorAll(TARGETS).forEach(queue);
    });
    if (!pending.length) return;

    const rules = collect();
    if (!rules.length) return;
    pending.forEach((element) => apply(rules, element));
  };

  // setTimeout, not requestAnimationFrame: a background tab never paints, and
  // the popup can be opened in one (a middle-click, a restored session).
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      run();
    }, 16);
  };

  schedule();
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
})();
